import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, GraduationCap, Briefcase, Award, Languages, FolderOpen, Wrench, Star, Trash2, Plus } from 'lucide-react';
import resumeAPI from '../../services/api';
import { RESUME_TEMPLATES } from '../../config/constants';
import { getFontStyle, getHeaderFontStyle } from '../../utils/fontSizeHelper';

const BASIC_INFO_FIELDS = [
  { key: 'full_name', label: 'Full Name' },
  { key: 'headline', label: 'Headline' },
  { key: 'credentials', label: 'Credentials' },
  { key: 'pronouns', label: 'Pronouns' },
  { key: 'professional_summary', label: 'Professional Summary' },
  { key: 'contact_email', label: 'Email' },
  { key: 'contact_phone', label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'linkedin_url', label: 'LinkedIn' },
  { key: 'website_url', label: 'Website' },
];

const EXPERIENCE_FIELDS = [
  { key: 'title', label: 'Job Title' },
  { key: 'company', label: 'Company' },
  { key: 'location', label: 'Location' },
  { key: 'description', label: 'Description' },
];

const EDUCATION_FIELDS = [
  { key: 'degree', label: 'Degree' },
  { key: 'institution', label: 'Institution' },
  { key: 'location', label: 'Location' },
  { key: 'description', label: 'Description' },
];

const FieldReassignDropdown = ({ currentField, fieldOptions, onReassign }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
        type="button"
      >
        {currentField}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
          {fieldOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => { onReassign(opt.key); setOpen(false); }}
              className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 ${
                opt.label === currentField ? 'font-semibold text-blue-700' : 'text-gray-700'
              }`}
              type="button"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const EditableField = ({ value, onChange, multiline, placeholder, className = '' }) => {
  const ref = useRef(null);

  const handleBlur = useCallback(() => {
    if (!ref.current) return;
    const newVal = multiline ? ref.current.innerText : ref.current.textContent;
    if (newVal !== value) onChange(newVal);
  }, [value, onChange, multiline]);

  const handleKeyDown = useCallback((e) => {
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
  }, [multiline]);

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`outline-none rounded px-1 -mx-1 hover:bg-blue-50 focus:bg-blue-50 focus:ring-2 focus:ring-blue-300 transition-colors ${className}`}
      style={{ minHeight: multiline ? '2em' : undefined, whiteSpace: multiline ? 'pre-wrap' : undefined }}
      data-placeholder={placeholder}
    >
      {value || ''}
    </div>
  );
};

const EditableResumePreview = ({ resume, updateResume, isImported }) => {
  const [importedHtml, setImportedHtml] = useState('');
  const [loadingHtml, setLoadingHtml] = useState(false);
  const highlightColor = resume?.highlight_color || '#3b82f6';
  const resumeId = resume?.id || resume?._id || resume?.resume_id;
  const isImportCustom = resume?.template === RESUME_TEMPLATES.IMPORT_CUSTOM;

  useEffect(() => {
    if (!isImportCustom || !resumeId) return;
    let cancelled = false;
    setLoadingHtml(true);
    resumeAPI.fetchImportedHtml(resumeId).then((html) => {
      if (!cancelled) setImportedHtml(html || '');
    }).catch(() => {}).finally(() => { if (!cancelled) setLoadingHtml(false); });
    return () => { cancelled = true; };
  }, [isImportCustom, resumeId]);

  const handleFieldUpdate = useCallback((field, value) => {
    updateResume(field, value);
  }, [updateResume]);

  const handleArrayFieldUpdate = useCallback((section, index, field, value) => {
    const arr = [...(resume[section] || [])];
    arr[index] = { ...arr[index], [field]: value };
    updateResume(section, arr);
  }, [resume, updateResume]);

  const handleReassign = useCallback((section, index, sourceField, targetField) => {
    if (section === 'basic') {
      const sourceValue = resume[sourceField] || '';
      const targetValue = resume[targetField] || '';
      if (sourceValue && !targetValue) {
        updateResume(sourceField, '');
        updateResume(targetField, sourceValue);
      } else if (sourceValue && targetValue) {
        updateResume(targetField, targetValue + ' | ' + sourceValue);
        updateResume(sourceField, '');
      }
    } else {
      const arr = [...(resume[section] || [])];
      const item = { ...arr[index] };
      const sourceValue = item[sourceField] || '';
      const targetValue = item[targetField] || '';
      if (sourceValue && !targetValue) {
        item[targetField] = sourceValue;
        item[sourceField] = '';
      } else if (sourceValue) {
        item[targetField] = targetValue + ' | ' + sourceValue;
        item[sourceField] = '';
      }
      arr[index] = item;
      updateResume(section, arr);
    }
  }, [resume, updateResume]);

  const handleRemoveItem = useCallback((section, index) => {
    const arr = [...(resume[section] || [])];
    arr.splice(index, 1);
    updateResume(section, arr);
  }, [resume, updateResume]);

  const handleAddItem = useCallback((section) => {
    const arr = [...(resume[section] || [])];
    if (section === 'experience') arr.push({ title: '', company: '', description: '', location: '' });
    else if (section === 'education') arr.push({ degree: '', institution: '', description: '', location: '' });
    updateResume(section, arr);
  }, [resume, updateResume]);

  const experiences = resume.experience || [];
  const education = resume.education || [];
  const skills = resume.skills || [];
  const coreSkills = resume.core_skills || [];

  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {/* Banner */}
        {resume.banner_url && (
          <div className="h-28 bg-gray-200" style={{ backgroundImage: `url(${resume.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        )}

        <div className="p-8">
          {/* Header / Basic Info */}
          <div className="mb-6 border-b pb-6" style={{ borderColor: `${highlightColor}40`, ...getFontStyle(resume, 'header') }}>
            <div className="flex items-start gap-4">
              {resume.photo_url && (
                <img src={resume.photo_url} alt="" className="w-20 h-20 rounded-full object-cover border-2" style={{ borderColor: highlightColor }} />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {isImported && (
                    <FieldReassignDropdown
                      currentField="Full Name"
                      fieldOptions={BASIC_INFO_FIELDS}
                      onReassign={(targetKey) => handleReassign('basic', 0, 'full_name', targetKey)}
                    />
                  )}
                  <EditableField
                    value={resume.full_name || resume.title || ''}
                    onChange={(v) => handleFieldUpdate('full_name', v)}
                    placeholder="Full Name"
                    className="text-2xl font-bold text-gray-900"
                  />
                </div>

                <div className="flex items-center gap-2 mb-1">
                  {isImported && (
                    <FieldReassignDropdown
                      currentField="Headline"
                      fieldOptions={BASIC_INFO_FIELDS}
                      onReassign={(targetKey) => handleReassign('basic', 0, 'headline', targetKey)}
                    />
                  )}
                  <EditableField
                    value={resume.headline || ''}
                    onChange={(v) => handleFieldUpdate('headline', v)}
                    placeholder="Professional headline"
                    className="text-lg text-gray-600"
                  />
                </div>

                {(resume.credentials || isImported) && (
                  <div className="flex items-center gap-2 mb-1">
                    {isImported && (
                      <FieldReassignDropdown
                        currentField="Credentials"
                        fieldOptions={BASIC_INFO_FIELDS}
                        onReassign={(targetKey) => handleReassign('basic', 0, 'credentials', targetKey)}
                      />
                    )}
                    <EditableField
                      value={resume.credentials || ''}
                      onChange={(v) => handleFieldUpdate('credentials', v)}
                      placeholder="Credentials"
                      className="text-sm text-gray-500"
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                  {(resume.contact_email || isImported) && (
                    <EditableField
                      value={resume.contact_email || ''}
                      onChange={(v) => handleFieldUpdate('contact_email', v)}
                      placeholder="Email"
                    />
                  )}
                  {(resume.contact_phone || isImported) && (
                    <EditableField
                      value={resume.contact_phone || ''}
                      onChange={(v) => handleFieldUpdate('contact_phone', v)}
                      placeholder="Phone"
                    />
                  )}
                  {(resume.location || isImported) && (
                    <EditableField
                      value={resume.location || ''}
                      onChange={(v) => handleFieldUpdate('location', v)}
                      placeholder="Location"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Professional Summary */}
          {(resume.professional_summary || isImported) && (
            <div className="mb-6" style={getFontStyle(resume, 'summary')}>
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'summary') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                Professional Summary
              </h2>
              {isImported && (
                <FieldReassignDropdown
                  currentField="Professional Summary"
                  fieldOptions={BASIC_INFO_FIELDS}
                  onReassign={(targetKey) => handleReassign('basic', 0, 'professional_summary', targetKey)}
                />
              )}
              <EditableField
                value={resume.professional_summary || ''}
                onChange={(v) => handleFieldUpdate('professional_summary', v)}
                placeholder="Enter professional summary..."
                multiline
                className="text-gray-700 leading-relaxed"
              />
            </div>
          )}

          {/* Core Skills */}
          {coreSkills.length > 0 && (
            <div className="mb-6" style={getFontStyle(resume, 'core_skills')}>
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'core_skills') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <Star className="w-5 h-5" />
                Core Skills
              </h2>
              <div className="grid grid-cols-2 gap-1">
                {coreSkills.map((skill, i) => (
                  <div key={skill.id || i} className="flex items-center gap-1 text-sm text-gray-700">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: highlightColor }}></span>
                    <EditableField
                      value={skill.name || ''}
                      onChange={(v) => {
                        const arr = [...coreSkills];
                        arr[i] = { ...arr[i], name: v };
                        updateResume('core_skills', arr);
                      }}
                      placeholder="Skill"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Work Experience */}
          {experiences.length > 0 && (
            <div className="mb-6" style={getFontStyle(resume, 'experience')}>
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'experience') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <Briefcase className="w-5 h-5" />
                Work Experience
              </h2>
              <div className="space-y-5">
                {experiences.map((exp, index) => (
                  <div key={index} className="relative group pl-4 border-l-2" style={{ borderColor: `${highlightColor}60` }}>
                    <button
                      onClick={() => handleRemoveItem('experience', index)}
                      className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 transition-all"
                      type="button"
                      title="Remove entry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-2">
                      {isImported && (
                        <FieldReassignDropdown
                          currentField="Job Title"
                          fieldOptions={EXPERIENCE_FIELDS}
                          onReassign={(targetKey) => handleReassign('experience', index, 'title', targetKey)}
                        />
                      )}
                      <EditableField
                        value={exp.title || ''}
                        onChange={(v) => handleArrayFieldUpdate('experience', index, 'title', v)}
                        placeholder="Job Title"
                        className="text-lg font-semibold text-gray-900"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {isImported && (
                        <FieldReassignDropdown
                          currentField="Company"
                          fieldOptions={EXPERIENCE_FIELDS}
                          onReassign={(targetKey) => handleReassign('experience', index, 'company', targetKey)}
                        />
                      )}
                      <EditableField
                        value={exp.company || ''}
                        onChange={(v) => handleArrayFieldUpdate('experience', index, 'company', v)}
                        placeholder="Company"
                        className="text-gray-700 font-medium"
                      />
                    </div>
                    <div className="flex gap-4 text-sm text-gray-500 mt-1">
                      <EditableField
                        value={[exp.start_month, exp.start_year].filter(Boolean).join(' ') || ''}
                        onChange={(v) => {
                          const parts = v.trim().split(/\s+/);
                          const update = {};
                          if (parts.length >= 2) { update.start_month = parts[0]; update.start_year = parts[1]; }
                          else if (/^\d{4}$/.test(parts[0])) { update.start_year = parts[0]; update.start_month = ''; }
                          else { update.start_month = parts[0] || ''; }
                          const arr = [...experiences]; arr[index] = { ...arr[index], ...update }; updateResume('experience', arr);
                        }}
                        placeholder="Start date"
                      />
                      <span>-</span>
                      <EditableField
                        value={exp.is_current ? 'Present' : [exp.end_month, exp.end_year].filter(Boolean).join(' ') || ''}
                        onChange={(v) => {
                          const lower = v.trim().toLowerCase();
                          const update = {};
                          if (['present', 'current', 'now'].includes(lower)) {
                            update.is_current = true; update.end_month = ''; update.end_year = '';
                          } else {
                            update.is_current = false;
                            const parts = v.trim().split(/\s+/);
                            if (parts.length >= 2) { update.end_month = parts[0]; update.end_year = parts[1]; }
                            else if (/^\d{4}$/.test(parts[0])) { update.end_year = parts[0]; update.end_month = ''; }
                          }
                          const arr = [...experiences]; arr[index] = { ...arr[index], ...update }; updateResume('experience', arr);
                        }}
                        placeholder="End date"
                      />
                      {exp.location !== undefined && (
                        <EditableField
                          value={exp.location || ''}
                          onChange={(v) => handleArrayFieldUpdate('experience', index, 'location', v)}
                          placeholder="Location"
                        />
                      )}
                    </div>
                    <div className="mt-2">
                      {isImported && (
                        <FieldReassignDropdown
                          currentField="Description"
                          fieldOptions={EXPERIENCE_FIELDS}
                          onReassign={(targetKey) => handleReassign('experience', index, 'description', targetKey)}
                        />
                      )}
                      <EditableField
                        value={exp.description || ''}
                        onChange={(v) => handleArrayFieldUpdate('experience', index, 'description', v)}
                        placeholder="Job description, achievements, and responsibilities..."
                        multiline
                        className="text-sm text-gray-700 leading-relaxed"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleAddItem('experience')}
                className="mt-3 flex items-center gap-1 text-sm font-medium hover:underline"
                style={{ color: highlightColor }}
                type="button"
              >
                <Plus className="w-4 h-4" /> Add Experience
              </button>
            </div>
          )}

          {/* Education */}
          {education.length > 0 && (
            <div className="mb-6" style={getFontStyle(resume, 'education')}>
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'education') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <GraduationCap className="w-5 h-5" />
                Education
              </h2>
              <div className="space-y-4">
                {education.map((edu, index) => (
                  <div key={index} className="relative group pl-4 border-l-2" style={{ borderColor: `${highlightColor}60` }}>
                    <button
                      onClick={() => handleRemoveItem('education', index)}
                      className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 transition-all"
                      type="button"
                      title="Remove entry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-center gap-2">
                      {isImported && (
                        <FieldReassignDropdown
                          currentField="Degree"
                          fieldOptions={EDUCATION_FIELDS}
                          onReassign={(targetKey) => handleReassign('education', index, 'degree', targetKey)}
                        />
                      )}
                      <EditableField
                        value={edu.degree || ''}
                        onChange={(v) => handleArrayFieldUpdate('education', index, 'degree', v)}
                        placeholder="Degree"
                        className="text-lg font-semibold text-gray-900"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {isImported && (
                        <FieldReassignDropdown
                          currentField="Institution"
                          fieldOptions={EDUCATION_FIELDS}
                          onReassign={(targetKey) => handleReassign('education', index, 'institution', targetKey)}
                        />
                      )}
                      <EditableField
                        value={edu.institution || ''}
                        onChange={(v) => handleArrayFieldUpdate('education', index, 'institution', v)}
                        placeholder="Institution"
                        className="text-gray-700 font-medium"
                      />
                    </div>
                    <div className="flex gap-4 text-sm text-gray-500 mt-1">
                      <EditableField
                        value={edu.start_year || ''}
                        onChange={(v) => handleArrayFieldUpdate('education', index, 'start_year', v)}
                        placeholder="Start year"
                      />
                      <span>-</span>
                      <EditableField
                        value={edu.end_year || ''}
                        onChange={(v) => handleArrayFieldUpdate('education', index, 'end_year', v)}
                        placeholder="End year"
                      />
                    </div>
                    {(edu.description || isImported) && (
                      <EditableField
                        value={edu.description || ''}
                        onChange={(v) => handleArrayFieldUpdate('education', index, 'description', v)}
                        placeholder="Description"
                        multiline
                        className="mt-1 text-sm text-gray-700"
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleAddItem('education')}
                className="mt-3 flex items-center gap-1 text-sm font-medium hover:underline"
                style={{ color: highlightColor }}
                type="button"
              >
                <Plus className="w-4 h-4" /> Add Education
              </button>
            </div>
          )}

          {/* Skills */}
          {skills.length > 0 && (
            <div className="mb-6" style={getFontStyle(resume, 'skills')}>
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'skills') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <Wrench className="w-5 h-5" />
                Skills
              </h2>
              <EditableField
                value={skills.join(', ')}
                onChange={(v) => {
                  const newSkills = v.split(',').map((s) => s.trim()).filter(Boolean);
                  updateResume('skills', newSkills);
                }}
                placeholder="Skills (comma-separated)"
                className="text-sm text-gray-700"
              />
            </div>
          )}

          {/* Certifications */}
          {(resume.certifications || []).length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <Award className="w-5 h-5" />
                Certifications
              </h2>
              <EditableField
                value={(resume.certifications || []).map((c) => typeof c === 'string' ? c : c.name).join(', ')}
                onChange={(v) => {
                  const certs = v.split(',').map((s) => s.trim()).filter(Boolean);
                  updateResume('certifications', certs);
                }}
                placeholder="Certifications (comma-separated)"
                className="text-sm text-gray-700"
              />
            </div>
          )}

          {/* Languages */}
          {(resume.languages || []).length > 0 && (
            <div className="mb-6" style={getFontStyle(resume, 'languages')}>
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor, ...getHeaderFontStyle(resume, 'languages') }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <Languages className="w-5 h-5" />
                Languages
              </h2>
              <EditableField
                value={(resume.languages || []).map((l) => typeof l === 'string' ? l : l.name).join(', ')}
                onChange={(v) => {
                  const langs = v.split(',').map((s) => s.trim()).filter(Boolean);
                  updateResume('languages', langs);
                }}
                placeholder="Languages (comma-separated)"
                className="text-sm text-gray-700"
              />
            </div>
          )}

          {/* Awards */}
          {(resume.awards || []).length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" style={{ color: highlightColor }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                Awards
              </h2>
              <EditableField
                value={(resume.awards || []).join(', ')}
                onChange={(v) => {
                  const awards = v.split(',').map((s) => s.trim()).filter(Boolean);
                  updateResume('awards', awards);
                }}
                placeholder="Awards (comma-separated)"
                className="text-sm text-gray-700"
              />
            </div>
          )}

          {/* Imported HTML (read-only reference at bottom) */}
          {isImportCustom && importedHtml && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2" style={{ color: highlightColor }}>
                <div className="w-1 h-5" style={{ backgroundColor: highlightColor }}></div>
                <FolderOpen className="w-5 h-5" />
                Original Document (Reference)
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="prose max-w-none text-sm" dangerouslySetInnerHTML={{ __html: importedHtml }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditableResumePreview;
